import {api} from "@/shared/api";
import {Integrations} from "../integrations";
import {FormUtil} from "@/shared/forms/formUtil";
import {AddisonSyncConfig} from "./addisonSync";
import {htmlEscape} from "@/shared/utils/string";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Input from "@/shared/utils/input";
import {copyOfObject} from "@/shared/utils/misc";
import {AddisonOrganization, FieldError, Integration} from "@/shared/timr";
import {IntegrationType} from "@/shared/timrTypes";

const template = `
	<div id="addisonSyncConfigDialog">
		<form id="addisonSyncConfigForm" class="form-horizontal">
			<div class="form-group">
				<div class="col-xs-12">
					<div id="addisonSyncOrganizationSelectionList" class="panel panel-default mt-10" data-visible-logged-in>
						<p class="panel-title p-10 pb-0 text-bold">${messages["integrations.addisonSync.organizations.title"]}</p>
						<div class="panel-body p-10">
							<div id="addisonSyncOrganizationContainer" class="mt-15">
								<!-- Add organizations here -->
							</div>
						</div>
					</div>
				</div>
			</div>
		</form>
	</div>
	`;

type OrganizationWithRemovedFlag = { id: string; name: string; removed?: boolean };

export class AddisonSyncConfigDialog extends HTMLElement {

	private dom: JQuery;
	private modal: BootstrapModal.Modal;

	private config: AddisonSyncConfig;

	private editMode: boolean;
	private editIntegration: Integration;

	private loadOrganizationsRequest: JQueryXHR;

	public static create() {
		const dialog = document.createElement("addison_sync-config-dialog") as AddisonSyncConfigDialog;
		$("body").append(dialog);
		dialog.create();
		dialog.remove();
	}

	public static edit(integration: Integration, configuration: AddisonSyncConfig) {
		const dialog = document.createElement("addison_sync-config-dialog") as AddisonSyncConfigDialog;
		$("body").append(dialog);
		dialog.edit(integration, configuration);
		dialog.remove();
	}

	private connectedCallback() {
		this.innerHTML = template;

		this.dom = $("#addisonSyncConfigDialog");
		this.modal = new BootstrapModal.Modal({
			title: `<img src="/timr/images/integrations/addison_sync.svg" alt="${messages["integrations.addisonSync.title"]}" width="100px">`,
			escapeText: false,
			message: this.dom,
			destroyOnClose: true,
			buttons: [
				{
					text: "dialog.save",
					css: "saveButton btn-primary",
					action: () => {
						if (!this.editMode) {
							Integrations.triggerAddedIntegrationEvent(IntegrationType.ADDISON_SYNC, this.convertFormToJSONString($("#addisonSyncConfigForm")));
						} else {
							Integrations.triggerEditedIntegrationEvent(IntegrationType.ADDISON_SYNC, this.editIntegration, this.convertFormToJSONString($("#addisonSyncConfigForm")));
						}
					}
				},
				{
					text: "dialog.cancel",
					css: "cancelButton btn-default"
				},
				{
					text: "dialog.delete",
					css: "deleteButton btn-danger",
					left: true,
					action: (m) => {
						BootstrapModal.confirm({
							title: messages["dialog.delete.confirm"],
							message: messages["integrations.addisonSync.delete.message"],
							yesAction: (confirmModal) => {
								confirmModal.close();
								m.close();
								Integrations.triggerDeletedIntegrationEvent(IntegrationType.ADDISON_SYNC, this.editIntegration.id);
							}
						});
					}
				}
			],
			onOpen: (m) => {
				Input.toggle(m.find(".deleteButton"), this.editMode);
				m.find("#addisonSyncUsername").focus();
				Integrations.onSaveIntegration(IntegrationType.ADDISON_SYNC, (e, fieldErrors: FieldError[]) => {
					if (!fieldErrors) {
						m.close();
					} else {
						FormUtil.markFieldErrors($("#addisonSyncConfigForm"), fieldErrors);
						m.resetLoadingButtons();
					}
				});
			}
		});

		this.init();
	}

	private init() {
		this.initPopup();
	}

	private create() {
		this.editMode = false;
		this.resetModal();
		this.modal.show();
	}

	private edit(editIntegration: Integration, config: AddisonSyncConfig) {
		this.editIntegration = editIntegration;
		this.config = copyOfObject(config);
		this.editMode = true;
		this.resetModal(config);
		this.loadOrganizations();
		this.modal.show();
	}

	private initPopup() {
		$("[data-open-info-popup]").popover({
			trigger: "hover",
			container: ".content-wrapper",
			delay: {show: 500, hide: 0},
			placement: "top",
			html: true,
			template: `<div class="popover width-150" role="tooltip">
								<div class="arrow"></div>
								<div class="popover-content"></div>
							</div>`,
			content: function () {
				const content = $(this).data("open-info-popup") as string;
				return `<div>${messages[content]}</div>`;
			}
		} as PopoverOptions);
	}

	private resetModal(config?: AddisonSyncConfig) {
		this.dom.find("[data-visible-logged-in]").toggle(this.editMode);
	}

	private loadOrganizations() {
		this.loadOrganizationsRequest?.abort();
		this.loadOrganizationsRequest = api("integrations/addison-sync/organizations.json")
			.get({
				id: this.editIntegration?.id
			})
			.done((response: AddisonOrganization[]) => {
				const container = this.dom.find("#addisonSyncOrganizationContainer");
				container.empty();
				container.append(this.createOrganizationHeader());
				const organizationsScrollContainer = $("<div>")
					.addClass("scroll-container restrict-height");
				// Add all organizations and mark the ones that are no longer available
				const organizations: OrganizationWithRemovedFlag[] = response.map((org) => ({...org}));
				if (this.config) {
					organizations.push(...(this.config.activeOrganizations || [])
						.filter((org) => !organizations.some((o) => o.id === org.id))
						.map((org) => ({...org, removed: true}))
					);
				}
				organizations.sort((a, b) => a.name.localeCompare(b.name));
				organizations.forEach((type) => {
					const checkbox = this.createOrganizationCheckbox(type);
					organizationsScrollContainer.append(checkbox);
				});
				container.append(organizationsScrollContainer);
			});
	}

	private createOrganizationHeader() {
		return $(`
				<div class="flex-row justify-content-space-between scroll-container">
					<p class="text-bold">${messages["integrations.addisonSync.organization"]}</p>
					<p class="text-bold">${messages["integrations.addisonSync.integrationActive"]}</p>
				</div>
			`);
	}

	private createOrganizationCheckbox(organization: OrganizationWithRemovedFlag): JQuery {
		const organizationName = htmlEscape(organization.name);
		return $(`
				<div class="flex-row justify-content-space-between">
					<label for="addisonSyncOrganization-${organization.id}" class="${organization.removed ? "text-danger" : ""}">${organizationName}</label>
					<input type="checkbox"
						id="addisonSyncOrganization-${organization.id}"
						value="${organization.id}"
						data-organization="${organizationName}"
						${(this.config?.activeOrganizations || []).some((org) => org.id === organization.id) ? "checked" : ""}
					/> 
				</div>
			`);
	}

	private findAllSelectedOrganizations(): AddisonOrganization[] {
		return this.dom.find("[data-organization]:checked").toArray().map((elem: HTMLElement) => ({
			id: $(elem).val() as string,
			name: $(elem).data("organization") as string
		}));
	}

	private convertFormToJSONString(form: JQuery): string {
		type ParamsType = { name: string, value: object | string };
		const params: ParamsType[] = [];
		params.push({name: "activeOrganizations", value: this.findAllSelectedOrganizations()});

		const json: Record<string, object | string> = {};
		$.each(params, (i, param: ParamsType) => {
			json[param.name] = param.value || "";
		});
		return JSON.stringify(json);
	}
}
